import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import posthog from 'posthog-js'
import { getFundInvite } from 'services/messagingTopics'
import { faPlain } from 'utilities/helper/format'

/** The card goes away once this share of the fund is on a messenger. */
export const GOOD_REACH = 0.8

const dismissKey = (moneyPool: string, place: string) =>
    `hamyan.inviteCardDismissed.${place}.${moneyPool}`

const isDismissed = (moneyPool: string, place: string) => {
    try {
        return window.localStorage.getItem(dismissKey(moneyPool, place)) === '1'
    } catch {
        return false
    }
}

/**
 * «N عضو هنوز در بله/ایتا نیستند» — wherever a manager meets reach or cost
 * (the fund page, «اطلاع‌رسانی به اعضا», buying SMS), the invite is offered
 * right there, with the manager's own trackable link (`/fund/invite/<id>`).
 * Managers only; gone at 80% reach; the fund page's copy can be closed.
 */
export default function InviteNudgeCard({
    moneyPool,
    isManager,
    place,
    dismissible = false,
}: {
    moneyPool: string | undefined
    isManager: boolean
    /** where it is shown, for the event and the per-place dismissal */
    place: 'fund_page' | 'announce' | 'buy_sms'
    dismissible?: boolean
}) {
    const [dismissed, setDismissed] = useState(
        () => !!moneyPool && dismissible && isDismissed(moneyPool, place)
    )
    const { data } = useQuery(
        ['fund-invite', moneyPool],
        () => getFundInvite(moneyPool as string),
        { enabled: isManager && !!moneyPool && !dismissed, staleTime: 300_000 }
    )
    if (!isManager || !moneyPool || dismissed || !data) return null
    const { reach } = data
    if (!reach.off || reach.messenger >= GOOD_REACH * reach.total) return null

    const close = () => {
        setDismissed(true)
        try {
            window.localStorage.setItem(dismissKey(moneyPool, place), '1')
        } catch {
            /* shown again next time */
        }
        posthog.capture('invite_card_dismissed', {
            fund_id: Number(moneyPool),
            place,
        })
    }

    return (
        <div
            className="relative mx-3 mt-3 rounded-xl border border-brand-soft bg-brand-soft p-3 text-xs leading-6 text-text-body"
            data-testid="invite-card"
        >
            {dismissible && (
                <button
                    type="button"
                    onClick={close}
                    aria-label="بستن"
                    className="absolute left-2 top-2 text-text-secondary"
                >
                    ✕
                </button>
            )}
            <p className="font-bold text-brand-deep">
                📱 {faPlain.format(reach.messenger)} از{' '}
                {faPlain.format(reach.total)} عضو در بله یا ایتا هستند
            </p>
            <p className="mt-1">
                {place === 'buy_sms'
                    ? `اعضایی که به بله/ایتا بیایند دیگر پیامک لازم ندارند؛ ${faPlain.format(
                          reach.off
                      )} عضو شما هنوز پیامکی‌اند.`
                    : `${faPlain.format(
                          reach.off
                      )} عضو هنوز نه؛ در بله و ایتا خبرها رایگان و همان لحظه می‌رسد.`}
            </p>
            <Link
                to={`/fund/invite/${moneyPool}`}
                onClick={() =>
                    posthog.capture('invite_card_clicked', {
                        fund_id: Number(moneyPool),
                        place,
                    })
                }
                className="mt-2 inline-block font-bold text-brand"
            >
                لینک دعوت را در گروه صندوق بفرستید ←
            </Link>
        </div>
    )
}
